import type { KeyboardEvent } from 'react';
import { memo, useCallback, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import type { ITheme } from '../../../utils/theme';
import { TypographyVariant } from '../../../utils/theme';
import Icon, { IconName } from '../../icon/icon';
import { IconScale } from '../../icon/types';
import Typography from '../../typography/typography';

const Chip = ({
  onClick,
  text,
  hidden,
  closeButtonLabel,
  disabled = false,
}: {
  onClick?: () => void;
  text: string;
  hidden?: boolean;
  closeButtonLabel?: string;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onClick?.();
    },
    [onClick]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter') {
        onClick?.();
        event.stopPropagation();
      }
    },
    [onClick]
  );

  return (
    <ChipWrapper $hidden={Boolean(hidden)} $isFocused={isFocused}>
      <ChipText
        variant={TypographyVariant.bodyMd2}
        color={disabled ? theme.palette.neutral[70] : theme.palette.neutral[90]}
        aria-selected={true}
      >
        {text}
      </ChipText>
      {onClick && (
        <CrossWrapper
          id="chip-cross"
          onMouseDown={handleMouseDown}
          onMouseEnter={() => setIsFocused(true)}
          onMouseLeave={() => setIsFocused(false)}
          tabIndex={0}
          role="button"
          onKeyDown={handleKeyDown}
          aria-label={closeButtonLabel ?? `Remove ${text} from selection`}
        >
          <Icon
            name={IconName.Close}
            scale={IconScale.S}
            color={
              disabled ? theme.palette.neutral[70] : theme.palette.neutral[90]
            }
            className="icon-close"
          />
        </CrossWrapper>
      )}
    </ChipWrapper>
  );
};

export default memo(Chip);

const ChipWrapper = styled.div`
  ${({
    theme,
    $hidden,
    $isFocused,
  }: {
    theme: ITheme;
    $hidden: boolean;
    $isFocused: boolean;
  }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 22px;
    background-color: ${$isFocused
      ? theme.palette.neutral['50']
      : theme.palette.neutral['40']};
    padding: ${theme.spacing(1)};
    border-radius: ${theme.borderRadius.sm};
    visibility: ${$hidden ? 'hidden' : 'visible'};
    max-width: 100%;
    border: 1px solid ${theme.palette.neutral[70]};
  `};
`;
const CrossWrapper = styled.div`
  cursor: pointer;
  width: 16px;
  height: 16px;

  .icon-close {
    line-height: 12px;
  }
`;
export const ChipText = styled(Typography)`
  ${({ theme }) => css`
    padding-right: ${theme.spacing(1)}} ;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  `}
`;
